import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '@/context/CartContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Truck, Store } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Checkout = () => {
  const { cart, cartTotal, clearCart } = useCart();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    shipping_address: '',
    shipping_method: 'delivery'
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (cart.length === 0) {
      toast.error('Your cart is empty!');
      return;
    }

    try {
      setLoading(true);

      // Create order
      const orderResponse = await axios.post(`${API}/orders`, {
        ...formData,
        items: cart
      });

      const orderId = orderResponse.data.id;

      // Create checkout session
      const checkoutResponse = await axios.post(`${API}/checkout/session`, {
        order_id: orderId,
        origin_url: window.location.origin
      });

      // Redirect to Stripe
      window.location.href = checkoutResponse.data.url;
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to process checkout. Please try again.');
      setLoading(false);
    }
  };

  const totalBottles = cart.reduce((sum, item) => sum + item.quantity, 0);
  const shippingCost = formData.shipping_method === 'pickup' ? 0 : (totalBottles < 4 ? 4.95 : 8.95);
  const total = cartTotal + shippingCost;

  if (cart.length === 0) {
    navigate('/cart');
    return null;
  }

  return (
    <div className="bg-off_white min-h-screen py-12" data-testid="checkout-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="font-chewy text-5xl sm:text-7xl text-center mb-12">
          <span className="text-brand_blue">CHECKOUT</span>
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Checkout Form */}
          <form onSubmit={handleSubmit} className="lg:col-span-2 space-y-6">
            {/* Customer Info */}
            <div className="bg-white border-4 border-black rounded-xl p-6 shadow-hard">
              <h2 className="font-chewy text-3xl text-brand_blue mb-6">Customer Information</h2>
              <div className="space-y-4">
                <div>
                  <label className="font-nunito font-bold block mb-2">Full Name *</label>
                  <input
                    type="text"
                    name="customer_name"
                    value={formData.customer_name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-black rounded-lg font-nunito focus:ring-2 focus:ring-brand_blue focus:ring-offset-2"
                    data-testid="input-name"
                  />
                </div>
                <div>
                  <label className="font-nunito font-bold block mb-2">Email *</label>
                  <input
                    type="email"
                    name="customer_email"
                    value={formData.customer_email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-black rounded-lg font-nunito focus:ring-2 focus:ring-brand_blue focus:ring-offset-2"
                    data-testid="input-email"
                  />
                </div>
                <div>
                  <label className="font-nunito font-bold block mb-2">Phone</label>
                  <input
                    type="tel"
                    name="customer_phone"
                    value={formData.customer_phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-black rounded-lg font-nunito focus:ring-2 focus:ring-brand_blue focus:ring-offset-2"
                    data-testid="input-phone"
                  />
                </div>
              </div>
            </div>

            {/* Shipping Method */}
            <div className="bg-white border-4 border-black rounded-xl p-6 shadow-hard">
              <h2 className="font-chewy text-3xl text-brand_blue mb-6">Delivery Method</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label
                  className={`cursor-pointer border-4 border-black rounded-xl p-6 flex flex-col items-center gap-3 transition-all ${
                    formData.shipping_method === 'delivery'
                      ? 'bg-brand_blue text-white shadow-hard-lg'
                      : 'bg-white hover:shadow-hard'
                  }`}
                >
                  <input
                    type="radio"
                    name="shipping_method"
                    value="delivery"
                    checked={formData.shipping_method === 'delivery'}
                    onChange={handleChange}
                    className="sr-only"
                    data-testid="shipping-delivery"
                  />
                  <Truck className="w-12 h-12 pointer-events-none" strokeWidth={3} />
                  <span className="font-chewy text-2xl pointer-events-none">Delivery</span>
                  <span className="font-nunito font-bold pointer-events-none">$10.00</span>
                </label>

                <label
                  className={`cursor-pointer border-4 border-black rounded-xl p-6 flex flex-col items-center gap-3 transition-all ${
                    formData.shipping_method === 'pickup'
                      ? 'bg-brand_blue text-white shadow-hard-lg'
                      : 'bg-white hover:shadow-hard'
                  }`}
                >
                  <input
                    type="radio"
                    name="shipping_method"
                    value="pickup"
                    checked={formData.shipping_method === 'pickup'}
                    onChange={handleChange}
                    className="sr-only"
                    data-testid="shipping-pickup"
                  />
                  <Store className="w-12 h-12 pointer-events-none" strokeWidth={3} />
                  <span className="font-chewy text-2xl pointer-events-none">Local Pickup</span>
                  <span className="font-nunito font-bold pointer-events-none">FREE</span>
                </label>
              </div>
            </div>

            {/* Shipping Address */}
            {formData.shipping_method === 'delivery' && (
              <div className="bg-white border-4 border-black rounded-xl p-6 shadow-hard">
                <h2 className="font-chewy text-3xl text-brand_blue mb-6">Shipping Address</h2>
                <div>
                  <label className="font-nunito font-bold block mb-2">Address *</label>
                  <textarea
                    name="shipping_address"
                    value={formData.shipping_address}
                    onChange={handleChange}
                    required={formData.shipping_method === 'delivery'}
                    rows="3"
                    className="w-full px-4 py-3 border-2 border-black rounded-lg font-nunito focus:ring-2 focus:ring-brand_blue focus:ring-offset-2"
                    placeholder="Street address, city, state, ZIP"
                    data-testid="input-address"
                  />
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-brand_blue text-white font-chewy text-2xl px-8 py-4 rounded-full border-4 border-black shadow-hard-lg hover:shadow-hard-hover hover:-translate-y-1 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="place-order-button"
            >
              {loading ? 'PROCESSING...' : 'PLACE ORDER'}
            </button>
          </form>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white border-4 border-black rounded-xl p-6 shadow-hard sticky top-24">
              <h2 className="font-chewy text-3xl text-brand_blue mb-6">Order Summary</h2>
              <div className="space-y-3 mb-6">
                {cart.map((item) => (
                  <div key={item.product_id} className="flex justify-between font-nunito">
                    <span>
                      {item.name} x {item.quantity}
                    </span>
                    <span className="font-bold">${(item.price * item.quantity).toFixed(2)}</span>
                  </div>
                ))}
              </div>
              <div className="border-t-2 border-black pt-4 space-y-2 mb-6">
                <div className="flex justify-between font-nunito font-semibold text-lg">
                  <span>Subtotal:</span>
                  <span data-testid="checkout-subtotal">${cartTotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-nunito font-semibold text-lg">
                  <span>Shipping:</span>
                  <span data-testid="checkout-shipping">${shippingCost.toFixed(2)}</span>
                </div>
                <div className="border-t-2 border-black pt-2">
                  <div className="flex justify-between font-chewy text-2xl text-brand_red">
                    <span>Total:</span>
                    <span data-testid="checkout-total">${total.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;